import { Component, Input, OnDestroy } from '@angular/core';
import {
  InterviewStatusWidget,
  Status,
  Priority,
  RoutePaths,
  ProfileStatusLabels,
} from '@app/shared/constants/global.enum';
import { Subject, interval, takeWhile, takeUntil } from 'rxjs';
import { Candidates, BreakdownRow } from '@app/shared/interfaces/candidates.model';
import { HiringManagerCandidatesService } from '@app/pages/hiring-manager/hiring-manager-candidates/hiring-manager-candidates.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Component({
  selector: 'app-interview-status-widget',
  templateUrl: './interview-status-widget.component.html',
  styleUrl: './interview-status-widget.component.scss',
})
export class InterviewStatusWidgetComponent implements OnDestroy {
  profileLabels = ProfileStatusLabels;
  allPositions: any[] = [];
  opened: boolean = false;
  fontSize_opentab = 1.2;
  statusWidget = InterviewStatusWidget;
  priority = Priority;
  interviewWidgetStatus = Status;
  interviewStatus: { label: string; values: number[]; color?: string }[] = [];
  levels = ['Level 1', 'Level 2', 'Level 3'];
  RoutePaths = RoutePaths;
  isDataLoaded: boolean = false;
  private stopPolling$ = new Subject<void>();

  @Input() set hiringIds(ids: any[]) {
    if (!ids?.length) return;
    this.stopPolling$.next();
    this.isDataLoaded = false;
    this.candidatesService.getAllCandidates(ids);
    this.pollCandidatesData();
  }
  candidates: Candidates[] = [];
  displayCandidatesData: Candidates[] = [];
  candidateBreakdown: BreakdownRow[] = [];
  private originalStatusCounts: Record<string, number> = {};
  L1PendingCount = 0;
  L2PendingCount = 0;
  L3PendingCount = 0;
  L1RejectedCount = 0;
  L2RejectedCount = 0;
  L3RejectedCount = 0;
  candidateDroppedCount = 0;
  onboardingInProgressCount = 0;
  joinedCount = 0;
  openCount = 0;
  profilesStatus: { label: string; value: number; color?: string }[] = [];

  constructor(
    private toaster: ToastrService,
    private candidatesService: HiringManagerCandidatesService,
    private router: Router,
  ) {}

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
  }

  private pollCandidatesData(): void {
    interval(100)
      .pipe(
        takeWhile(() => !this.isDataLoaded),
        takeUntil(this.stopPolling$),
      )
      .subscribe({
        next: () => {
          this.fetchCandidatesData();
        },
        error: (error) => {
          this.isDataLoaded = true;
          this.toaster.error(this.statusWidget.Error);
        },
      });
  }

  private fetchCandidatesData(): void {
    const candidateSignalData = this.candidatesService.candidatesSignal();
    const summary = candidateSignalData?.data;
    if (summary && typeof summary === 'object' && !Array.isArray(summary) && summary.statusCounts) {
      this.originalStatusCounts = summary.statusCounts;
      this.candidateBreakdown = summary.breakdown ?? [];
      this.updateCountsFromSummary(summary.statusCounts);
      this.interviewStatusData();
      this.isDataLoaded = true;
    }
  }

  private updateCountsFromSummary(statusCounts: Record<string, number>): void {
    this.L1PendingCount = statusCounts[this.interviewWidgetStatus.L1Pending] ?? 0;
    this.L2PendingCount = statusCounts[this.interviewWidgetStatus.L2Pending] ?? 0;
    this.L3PendingCount = statusCounts[this.interviewWidgetStatus.L3Pending] ?? 0;
    this.L1RejectedCount = statusCounts[this.interviewWidgetStatus.L1Rejected] ?? 0;
    this.L2RejectedCount = statusCounts[this.interviewWidgetStatus.L2Rejected] ?? 0;
    this.L3RejectedCount = statusCounts[this.interviewWidgetStatus.L3Rejected] ?? 0;
    this.candidateDroppedCount = statusCounts[this.interviewWidgetStatus.DroppedOut] ?? 0;
    this.onboardingInProgressCount = statusCounts[this.interviewWidgetStatus.InProgess] ?? 0;
    this.joinedCount = statusCounts[this.interviewWidgetStatus.Joined] ?? 0;
    this.openCount = statusCounts[this.interviewWidgetStatus.Open] ?? 0;
  }

  private updateCounts(): void {
    this.L1PendingCount = 0;
    this.L2PendingCount = 0;
    this.L3PendingCount = 0;
    this.L1RejectedCount = 0;
    this.L2RejectedCount = 0;
    this.L3RejectedCount = 0;
    this.candidateDroppedCount = 0;
    this.onboardingInProgressCount = 0;
    this.joinedCount = 0;
    this.openCount = 0;

    this.candidates?.forEach(({ status }) => {
      if (!status) return;
      if (status.includes(this.interviewWidgetStatus.L1Pending)) this.L1PendingCount++;
      else if (status.includes(this.interviewWidgetStatus.L2Pending)) this.L2PendingCount++;
      else if (status.includes(this.interviewWidgetStatus.L3Pending)) this.L3PendingCount++;
      else if (status.includes(this.interviewWidgetStatus.L1Rejected)) this.L1RejectedCount++;
      else if (status.includes(this.interviewWidgetStatus.L2Rejected)) this.L2RejectedCount++;
      else if (status.includes(this.interviewWidgetStatus.L3Rejected)) this.L3RejectedCount++;   
      if (status.includes(this.interviewWidgetStatus.DroppedOut)) this.candidateDroppedCount++;
      else if (status.includes(this.interviewWidgetStatus.InProgess)) this.onboardingInProgressCount++;
      else if (status.includes(this.interviewWidgetStatus.Joined)) this.joinedCount++;
      else if (status.includes(this.interviewWidgetStatus.Open)) this.openCount++;
    });
  }

  private interviewStatusData(): void {
    this.interviewStatus = [
      {
        label: this.statusWidget.Pending,
        values: [this.L1PendingCount, this.L2PendingCount, this.L3PendingCount],
      },
      {
        label: this.statusWidget.Rejected,
        values: [
          this.L1RejectedCount,
          this.L2RejectedCount,
          this.L3RejectedCount,
        ],
      },
    ];
    this.profilesStatus = [
      {
        label: 'Open',
        value: this.openCount,
      },
      {
        label: 'Onboarding In Progress',
        value: this.onboardingInProgressCount,
      },
      {
        label: 'Joined',
        value: this.joinedCount,
      },
      {
        label: 'Dropped',
        value: this.candidateDroppedCount,
      }
    ];
  }

  onApplyFilter(event: { statusCounts: Record<string, number> | null; buttonClick: string }) {
    if (event.buttonClick === 'apply') {
      this.opened = false;
    }
    this.updateCountsFromSummary(event.statusCounts ?? this.originalStatusCounts);
    this.interviewStatusData();
  }

  getStatusStyle(status: any) {
    if (status.label === this.statusWidget.Pending) {
      return {
        color: this.statusWidget.orange,
      };
    } else if (status.label === this.statusWidget.Rejected) {
      return {
        color: this.statusWidget.red,
      };
    }
    return {
      color: status.color,
    };
  }

  navigateToPositionsList() {
    this.router.navigate(['/' + this.RoutePaths.HireMgr], { queryParams: { tab: 'candidates' } });
  }

  getLevelTotal(levelIndex: number): number {
    return this.interviewStatus.reduce((sum, row) => sum + (row.values[levelIndex] || 0), 0);
  }

  getLevelSegmentPct(levelIndex: number, rowIndex: number): number {
    const total = this.getLevelTotal(levelIndex);
    if (total <= 0) return 0;
    return (this.interviewStatus[rowIndex].values[levelIndex] / total) * 100;
  }

  get profilesTotal(): number {
    return this.openCount + this.onboardingInProgressCount + this.joinedCount + this.candidateDroppedCount;
  }

  profilePct(value: number): number {
    if (this.profilesTotal <= 0) return 0;
    return (value / this.profilesTotal) * 100;
  }
}
